/**
 * Outfits — ask for suggestions, keep the ones that work.
 *
 * Two lists on one screen, deliberately: suggestions are ephemeral drafts, saved outfits
 * are the thing you plan from. Nothing is written until "Keep" is pressed, which is the
 * same rule the insurance quote form follows — the model proposes, a person decides.
 *
 * A slot the wardrobe cannot fill comes back as a WANT rather than a failure. Keeping the
 * outfit turns that want into a gap row, and the gap list is then just a view over
 * outfits instead of a second list to maintain. That is the whole reason the engine is a
 * model and not a rules table: a rules table can only tell you an outfit is impossible.
 */
import { useMemo, useRef, useState } from 'react';
import { api } from '../../lib/api';
import type {
  GarmentLayer,
  LayerCoverage,
  Outfit,
  OutfitReach,
  OutfitSuggestion,
  OutfitSuggestionResult,
  Season,
  SuggestedSlot,
  TuckStyle,
  WardrobeOccasion,
} from '../../shared/types';
import {
  Button,
  Card,
  Chip,
  EmptyState,
  ErrorState,
  Loading,
  PanelHeading,
  Select,
  cx,
  errorText,
  photoSrc,
  swatchStyle,
  useResource,
} from './ui';

const SEASONS: Season[] = ['spring', 'summer', 'autumn', 'winter'];

/** The order a look is read in, top down. Slots render in this order regardless of input. */
const LAYER_ORDER: GarmentLayer[] = ['outer', 'mid', 'base', 'bottom', 'shoes', 'accessory'];

function bySlotOrder<T extends { slot?: GarmentLayer; layer?: GarmentLayer }>(a: T, b: T) {
  const ia = LAYER_ORDER.indexOf((a.slot ?? a.layer) as GarmentLayer);
  const ib = LAYER_ORDER.indexOf((b.slot ?? b.layer) as GarmentLayer);
  return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib);
}

/**
 * The tuck answer, in plain words.
 *
 * This gets its own line rather than a mention inside "Follows", because Follows is a
 * list of rules the outfit satisfies and the tuck is an instruction to carry out. Brian
 * kept missing it when the two were mixed together.
 */
const TUCK_COPY: Record<TuckStyle, { label: string; detail: string } | null> = {
  tucked: { label: 'Tuck it in', detail: 'Shirt fully tucked, belt showing.' },
  untucked: { label: 'Leave it out', detail: 'Hem worn out, over the waistband.' },
  front_tuck: {
    label: 'Front tuck',
    detail: 'Front hem tucked, back and sides left out.',
  },
  not_applicable: null,
};

function TuckLine({ tuck }: { tuck: TuckStyle | null }) {
  const copy = tuck ? TUCK_COPY[tuck] : null;
  if (!copy) return null;
  return (
    <p className="mt-3 flex flex-wrap items-baseline gap-x-2 rounded-lg bg-surface-sunk px-3 py-2 text-sm">
      <span className="font-semibold text-ink">{copy.label}</span>
      <span className="text-ink-soft">{copy.detail}</span>
    </p>
  );
}

/** Layers an outfit genuinely needs. An accessory at zero is not why you got two looks. */
const CORE_LAYERS: GarmentLayer[] = ['base', 'bottom', 'shoes'];

/**
 * The bottleneck, in one sentence.
 *
 * Brian asked whether two outfits meant a thin algorithm or a thin closet. It was the
 * closet — one eligible shoe and no summer layer — but nothing on screen said so, and a
 * short answer with no explanation reads as a weak engine every time.
 */
function CoverageNote({
  coverage,
  count,
}: {
  coverage: LayerCoverage[];
  count: number;
}) {
  const byLayer = new Map(coverage.map((c) => [c.layer, c.eligible]));
  const problems: string[] = [];

  for (const layer of CORE_LAYERS) {
    const n = byLayer.get(layer) ?? 0;
    if (n === 0) problems.push(`nothing in the ${layer} layer`);
    else if (n === 1) problems.push(`one ${layer} option`);
  }
  // The third layer is the defining rule of this style guide, so its absence is worth
  // naming even though an outfit is technically valid without one.
  if ((byLayer.get('mid') ?? 0) + (byLayer.get('outer') ?? 0) === 0) {
    problems.push('no layer to put over the top');
  }

  if (problems.length === 0) return null;

  const list =
    problems.length === 1
      ? problems[0]
      : `${problems.slice(0, -1).join(', ')} and ${problems[problems.length - 1]}`;

  return (
    <Card tone="near">
      <p className="max-w-prose text-sm leading-relaxed text-ink">
        <span className="font-semibold">
          {count === 0 ? 'No outfits' : count === 1 ? 'One outfit' : `${count} outfits`}
        </span>{' '}
        — for this occasion and season you have {list}. That is the limit, not the
        suggestions: more variety has to come from the wardrobe.
      </p>
      <p className="mt-2 text-xs text-ink-soft">
        {coverage.map((c) => `${c.layer} ${c.eligible}`).join(' · ')}
      </p>
    </Card>
  );
}

function currentSeason(): Season {
  const m = new Date().getMonth();
  if (m <= 1 || m === 11) return 'winter';
  if (m <= 4) return 'spring';
  if (m <= 7) return 'summer';
  return 'autumn';
}

export default function OutfitsPanel({ occasions }: { occasions: WardrobeOccasion[] }) {
  const [occasion, setOccasion] = useState(occasions[0]?.slug ?? 'weekend');
  const [season, setSeason] = useState<Season>(currentSeason());
  const [reach, setReach] = useState<OutfitReach>('shops');
  const [result, setResult] = useState<OutfitSuggestionResult | null>(null);
  const [thinking, setThinking] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [keeping, setKeeping] = useState<number | null>(null);
  const [building, setBuilding] = useState(false);
  const [built, setBuilt] = useState<string | null>(null);
  // 10 outfits x 4 seasons is 40 rows. Unfiltered that is a wall, not a wardrobe.
  const [showSeason, setShowSeason] = useState<Season | 'all'>('all');
  // Building writes ten rows to a list that lives BELOW the fold, and the only feedback
  // was a success card at the top. "Built 10 outfits" while the screen does not visibly
  // change reads as a lie. Scroll to the thing that changed.
  const savedRef = useRef<HTMLDivElement | null>(null);

  const saved = useResource<Outfit[]>(() => api.get<Outfit[]>('/wardrobe/outfits'), []);

  // An outfit with no seasons recorded is all-year, so it survives every filter.
  const visible = (saved.data ?? []).filter(
    (o) =>
      showSeason === 'all' || o.seasons.length === 0 || o.seasons.includes(showSeason)
  );

  async function suggest() {
    setThinking(true);
    setProblem(null);
    setResult(null);
    try {
      setResult(
        await api.post<OutfitSuggestionResult>('/wardrobe/outfits/suggest', {
          occasion,
          season,
          reach,
        })
      );
    } catch (err) {
      setProblem(errorText(err));
    } finally {
      setThinking(false);
    }
  }

  /**
   * `index`, not name. The model reuses names freely — three saved outfits are called "Black
   * graphic tee, light denim" — and identifying a suggestion by its name meant that keeping ONE
   * removed EVERY suggestion sharing that name from the list, and put the spinner on all of them
   * at once. That is a real disappearing-suggestions bug: return three, two share a name, keep
   * one, and one is left.
   */
  async function keep(s: OutfitSuggestion, index: number) {
    setKeeping(index);
    setProblem(null);
    try {
      await api.post<Outfit>('/wardrobe/outfits', {
        name: s.name,
        occasion: s.occasion,
        seasons: [season],
        reasoning: s.reasoning,
        tuck: s.tuck,
        slots: s.slots.map((sl) => ({
          slot: sl.layer,
          item_id: sl.item_id,
          wanted: sl.wanted,
        })),
      });
      setResult((r) =>
        r ? { ...r, suggestions: r.suggestions.filter((_, i) => i !== index) } : r
      );
      saved.reload();
    } catch (err) {
      setProblem(errorText(err));
    } finally {
      setKeeping(null);
    }
  }

  async function buildSeason() {
    setBuilding(true);
    setProblem(null);
    setBuilt(null);
    try {
      const r = await api.post<{
        season: Season;
        created: number;
        gaps_added: number;
      }>('/wardrobe/outfits/build-season', { season, count: 10 });
      setBuilt(
        `Built ${r.created} ${season} ${r.created === 1 ? 'outfit' : 'outfits'}` +
          (r.gaps_added > 0
            ? `, and added ${r.gaps_added} ${r.gaps_added === 1 ? 'thing' : 'things'} to the shopping list`
            : '') +
          `. They're in Saved outfits below, filtered to ${season}.`
      );
      setShowSeason(season);
      saved.reload();
      // After paint, so the list has grown before we scroll to it.
      requestAnimationFrame(() =>
        savedRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      );
    } catch (err) {
      setProblem(errorText(err));
    } finally {
      setBuilding(false);
    }
  }

  async function forget(o: Outfit) {
    await api.del(`/wardrobe/outfits/${o.id}`).catch(() => {});
    saved.reload();
  }

  return (
    <section className="space-y-6">
      <PanelHeading
        title="Outfits"
        sub="Built from what you own — and told plainly when something is missing."
      />

      <Card tone="sunk">
        <div className="flex flex-wrap items-end gap-3">
          <label className="text-sm text-ink-soft">
            <span className="mb-1 block">Occasion</span>
            <Select
              value={occasion}
              onChange={(e) => setOccasion(e.target.value)}
              className="w-auto"
            >
              {occasions.map((o) => (
                <option key={o.slug} value={o.slug}>
                  {o.label}
                </option>
              ))}
            </Select>
          </label>
          <label className="text-sm text-ink-soft">
            <span className="mb-1 block">Season</span>
            <Select
              value={season}
              onChange={(e) => setSeason(e.target.value as Season)}
              className="w-auto"
            >
              {SEASONS.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </Select>
          </label>
          <label className="text-sm text-ink-soft">
            <span className="mb-1 block">Build from</span>
            <Select
              value={reach}
              onChange={(e) => setReach(e.target.value as OutfitReach)}
              className="w-auto"
            >
              <option value="shops">Anything — including things to buy</option>
              <option value="closet">Only what I own</option>
            </Select>
          </label>
          <Button variant="primary" onClick={suggest} disabled={thinking || building}>
            {thinking ? 'Thinking…' : 'Suggest outfits'}
          </Button>
        </div>

        <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-border pt-4">
          <p className="max-w-prose text-sm text-ink-soft">
            Or plan the whole season at once &mdash; ten outfits, about two working weeks.
            Anything an outfit needs and you don&rsquo;t own goes straight onto the
            shopping list.
          </p>
          <Button onClick={buildSeason} disabled={building || thinking}>
            {building ? `Building ${season}…` : `Build 10 ${season} outfits`}
          </Button>
        </div>
      </Card>

      {built && !problem && (
        <Card tone="ok">
          <p className="text-sm text-ink">{built}</p>
        </Card>
      )}

      {problem && (
        <Card tone="over">
          <p className="text-sm text-ink">{problem}</p>
        </Card>
      )}

      {thinking && <Loading label="Putting outfits together" rows={2} />}

      {result && !thinking && reach === 'closet' && (
        <CoverageNote coverage={result.coverage ?? []} count={result.suggestions.length} />
      )}

      {/* A short answer must explain itself. Silently returning four when six were asked for
          reads as a weak engine; saying so points at the real cause, which is usually a thin
          layer or a shortage of genuinely different combinations left to make. */}
      {result && !thinking && result.suggestions.length < (result.asked_for ?? 0) && (
        <Card tone="near">
          <p className="text-sm text-ink">
            {result.suggestions.length} of the {result.asked_for} asked for.
            {result.repeats_dropped > 0 && (
              <>
                {' '}
                {result.repeats_dropped}{' '}
                {result.repeats_dropped === 1 ? 'was' : 'were'} dropped for repeating an outfit
                you have already kept.
              </>
            )}{' '}
            {reach === 'closet'
              ? 'Switching reach to shops lets it propose things to buy and usually fills the rest.'
              : 'Usually means this occasion and season are running out of genuinely different combinations.'}
          </p>
        </Card>
      )}

      {result && result.suggestions.length > 0 && (
        <div className="space-y-3">
          <h3 className="font-display text-lg font-semibold text-ink">
            Suggestions &mdash; not saved yet
          </h3>
          {/* Keyed by index as well as name because this model reuses names routinely. A bare
              duplicate key does NOT drop a sibling — checked, React renders both and warns —
              but it does make reconciliation attach state to the wrong card on re-render. */}
          {result.suggestions.map((s, i) => (
            <SuggestionCard
              key={`${i}-${s.name}`}
              suggestion={s}
              busy={keeping === i}
              onKeep={() => keep(s, i)}
            />
          ))}
        </div>
      )}

      {result && result.suggestions.length === 0 && !thinking && (
        <EmptyState title="Nothing came back for that combination">
          Try a different occasion or season. If it keeps happening, the wardrobe probably
          has no garments that fit the formality band.
        </EmptyState>
      )}

      {/* A dropped id means the model named a garment that is not in this wardrobe. It is
          discarded rather than rendered — surfaced here so a silent drop can't hide a bug. */}
      {result && result.rejected_ids.length > 0 && (
        <Card tone="near">
          <p className="text-sm text-ink">
            {result.rejected_ids.length} suggested{' '}
            {result.rejected_ids.length === 1 ? 'garment was' : 'garments were'} not in your
            wardrobe and {result.rejected_ids.length === 1 ? 'was' : 'were'} dropped.
          </p>
        </Card>
      )}

      <div ref={savedRef} className="scroll-mt-4">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <h3 className="font-display text-lg font-semibold text-ink">
            Saved outfits{visible.length > 0 && ` (${visible.length})`}
          </h3>
          {(saved.data?.length ?? 0) > 0 && (
            <Select
              value={showSeason}
              onChange={(e) => setShowSeason(e.target.value as Season | 'all')}
              className="w-auto"
              aria-label="Filter saved outfits by season"
            >
              <option value="all">All seasons</option>
              {SEASONS.map((sn) => (
                <option key={sn} value={sn}>
                  {sn}
                </option>
              ))}
            </Select>
          )}
        </div>
        {saved.loading && <Loading label="Loading outfits" rows={2} />}
        {saved.error && !saved.loading && (
          <ErrorState message={saved.error} onRetry={saved.reload} />
        )}
        {saved.data && !saved.loading && !saved.error && visible.length === 0 && (
          <EmptyState title="No outfits kept yet">
            Suggest a few above and keep the ones that look right. Saved outfits are what you
            drag onto the week in Plan.
          </EmptyState>
        )}
        {visible.length > 0 && (
          <div className="space-y-3">
            {visible.map((o) => (
              <SavedCard key={o.id} outfit={o} onForget={() => forget(o)} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------- slot tiles

/** One garment in a look. Photo when there is one, colour swatch when there isn't. */
function SlotTile({
  label,
  layer,
  itemId,
  hasImage,
  imageKey = null,
  missing,
}: {
  label: string;
  layer: GarmentLayer;
  itemId: number | null;
  hasImage: boolean;
  /** Only the saved-outfit path has this. A suggestion is not saved, so there is no key yet. */
  imageKey?: string | null;
  missing: boolean;
}) {
  // A photo can 404 even when the row says it has one — R2 object deleted, key rotated
  // mid-upload. Fall back to the swatch rather than showing a broken image.
  const [failed, setFailed] = useState(false);
  return (
    <li className="w-24 shrink-0">
      <div
        className={cx(
          'relative aspect-square w-24 overflow-hidden rounded-lg border',
          missing ? 'border-dashed border-clay/60 bg-clay-soft' : 'border-border bg-surface-sunk'
        )}
        style={!missing && !hasImage ? swatchStyle([]) : undefined}
      >
        {itemId !== null && hasImage && !failed ? (
          <img
            src={photoSrc({ id: itemId, image_r2_key: imageKey })}
            alt=""
            loading="lazy"
            onError={() => setFailed(true)}
            className="h-full w-full object-cover"
          />
        ) : (
          <span
            className={cx(
              'absolute inset-0 grid place-items-center px-1 text-center text-[10px] leading-tight',
              missing ? 'text-clay' : 'text-ink-soft'
            )}
          >
            {missing ? 'need to buy' : layer}
          </span>
        )}
      </div>
      <p className="mt-1 text-[11px] leading-tight text-ink-soft">{label}</p>
    </li>
  );
}

function SuggestionCard({
  suggestion,
  busy,
  onKeep,
}: {
  suggestion: OutfitSuggestion;
  busy: boolean;
  onKeep: () => void;
}) {
  const slots = [...suggestion.slots].sort(bySlotOrder);
  const wants = slots.filter((s) => s.wanted);

  return (
    <Card>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h4 className="font-display text-base font-semibold text-ink">{suggestion.name}</h4>
          <div className="mt-1 flex flex-wrap gap-1.5">
            {suggestion.wearable_today ? (
              <Chip tone="ok">Wearable today</Chip>
            ) : (
              <Chip tone="near">
                Needs {wants.length} {wants.length === 1 ? 'thing' : 'things'}
              </Chip>
            )}
          </div>
        </div>
        <Button variant="primary" onClick={onKeep} disabled={busy}>
          {busy ? 'Keeping…' : 'Keep'}
        </Button>
      </div>

      <ul className="mt-3 flex flex-wrap gap-3">
        {slots.map((s: SuggestedSlot, i) => (
          <SlotTile
            key={`${s.layer}-${i}`}
            label={s.item_name ?? s.wanted ?? s.layer}
            layer={s.layer}
            itemId={s.item_id}
            hasImage={s.has_image}
            missing={s.item_id === null}
          />
        ))}
      </ul>

      <TuckLine tuck={suggestion.tuck} />

      <p className="mt-3 max-w-prose text-sm leading-relaxed text-ink-soft">
        {suggestion.reasoning}
      </p>

      {suggestion.honours.length > 0 && (
        <p className="mt-2 text-xs leading-relaxed text-ink-soft">
          <span className="font-medium text-ink">Follows:</span> {suggestion.honours.join(' · ')}
        </p>
      )}
    </Card>
  );
}

/**
 * Put a saved outfit on a day without leaving this tab.
 *
 * Brian, 12 Aug 2026: "Lastly, on the outfits page or an ability to add an outfit to the calendar
 * from that page." Writes to the same PUT /wardrobe/plan the Plan tab uses, so the two screens
 * cannot disagree — there is one plan, reachable from either side.
 *
 * Fourteen days, because a wardrobe plan a month out is fiction. Today and tomorrow are named
 * rather than dated, since those are the two anybody actually picks.
 */
function AddToDay({ outfit }: { outfit: Outfit }) {
  const [saving, setSaving] = useState(false);
  const [placed, setPlaced] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  const days = useMemo(() => {
    const today = new Date();
    return Array.from({ length: 14 }, (_, i) => {
      const d = new Date(
        Date.UTC(today.getFullYear(), today.getMonth(), today.getDate() + i)
      );
      const iso = d.toISOString().slice(0, 10);
      const name =
        i === 0
          ? 'Today'
          : i === 1
            ? 'Tomorrow'
            : d.toLocaleDateString('en-GB', {
                weekday: 'short',
                day: 'numeric',
                month: 'short',
                timeZone: 'UTC',
              });
      return { iso, name };
    });
  }, []);

  async function put(iso: string) {
    setSaving(true);
    setFailed(false);
    try {
      await api.put('/wardrobe/plan', { plan_date: iso, outfit_id: outfit.id });
      setPlaced(days.find((d) => d.iso === iso)?.name ?? iso);
    } catch {
      setFailed(true);
    } finally {
      setSaving(false);
    }
  }

  if (placed) {
    return (
      <p className="text-sm font-medium text-sage">
        On {placed}.{' '}
        <button
          type="button"
          onClick={() => setPlaced(null)}
          className="font-normal text-ink-soft underline underline-offset-2 hover:text-ink"
        >
          another day
        </button>
      </p>
    );
  }

  return (
    <label className="flex flex-wrap items-center gap-2 text-sm text-ink-soft">
      <span>Wear it on</span>
      <Select
        value=""
        disabled={saving}
        onChange={(e) => e.target.value && void put(e.target.value)}
        className="w-auto"
        aria-label={`Put ${outfit.name} on a day`}
      >
        <option value="">Pick a day…</option>
        {days.map((d) => (
          <option key={d.iso} value={d.iso}>
            {d.name}
          </option>
        ))}
      </Select>
      {failed && <span className="text-clay">Didn&rsquo;t save — try again.</span>}
    </label>
  );
}

function SavedCard({ outfit, onForget }: { outfit: Outfit; onForget: () => void }) {
  const slots = [...outfit.slots].sort(bySlotOrder);
  return (
    <Card>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h4 className="font-display text-base font-semibold text-ink">{outfit.name}</h4>
          <div className="mt-1 flex flex-wrap gap-1.5">
            {outfit.occasion && <Chip>{outfit.occasion.replace(/_/g, ' ')}</Chip>}
            {outfit.wearable_today ? (
              <Chip tone="ok">Wearable today</Chip>
            ) : (
              <Chip tone="near">Needs {outfit.missing_count}</Chip>
            )}
          </div>
        </div>
        <Button variant="danger" onClick={onForget}>
          Remove
        </Button>
      </div>

      <ul className="mt-3 flex flex-wrap gap-3">
        {slots.map((s) => (
          <SlotTile
            key={s.slot}
            label={s.item_name ?? s.gap_description ?? s.slot}
            layer={s.slot}
            itemId={s.item_id}
            hasImage={s.has_image}
            imageKey={s.image_r2_key}
            missing={s.item_id === null}
          />
        ))}
      </ul>

      <div className="mt-3">
        <AddToDay outfit={outfit} />
      </div>

      <TuckLine tuck={outfit.tuck} />

      {outfit.reasoning && (
        <p className="mt-3 max-w-prose text-sm leading-relaxed text-ink-soft">
          {outfit.reasoning}
        </p>
      )}
    </Card>
  );
}
